import { httpClient } from "@/lib/http";
import { FRAUD_ENDPOINTS } from "@/config/api";
import type { ApiResponse } from "@/lib/http";
import type {
    FraudAlertsQueryParams,
    FraudAlertsResponse,
    FraudAlertDetails,
    UpdateAlertStatusRequest,
    UpdateAlertStatusResponse,
    FraudStats,
    CasesQueryParams,
    CasesResponse,
    InvestigationCaseDetails,
    CreateCaseRequest,
    CreateCaseResponse,
    UpdateCaseRequest,
    UpdateCaseResponse,
    UserRiskScore,
    RequestReverificationRequest,
    RequestReverificationResponse,
    DetectionResponse,
    RunAllDetectionResponse,
} from "../types";

// ============================================================================
// Helpers
// ============================================================================

const buildAlertsQuery = (params: FraudAlertsQueryParams): string => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append("page", params.page.toString());
    if (params.limit !== undefined) q.append("limit", params.limit.toString());
    if (params.alertType) q.append("alertType", params.alertType);
    if (params.severity) q.append("severity", params.severity);
    if (params.status) q.append("status", params.status);
    if (params.userId) q.append("userId", params.userId);
    if (params.userType) q.append("userType", params.userType);
    if (params.startDate) q.append("startDate", params.startDate);
    if (params.endDate) q.append("endDate", params.endDate);
    return q.toString();
};

const buildCasesQuery = (params: CasesQueryParams): string => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append("page", params.page.toString());
    if (params.limit !== undefined) q.append("limit", params.limit.toString());
    if (params.caseType) q.append("caseType", params.caseType);
    if (params.priority) q.append("priority", params.priority);
    if (params.status) q.append("status", params.status);
    if (params.assignedTo) q.append("assignedTo", params.assignedTo);
    if (params.userId) q.append("userId", params.userId);
    if (params.startDate) q.append("startDate", params.startDate);
    if (params.endDate) q.append("endDate", params.endDate);
    return q.toString();
};

// ============================================================================
// Fraud Alerts API
// ============================================================================

export const getFraudAlerts = async (params: FraudAlertsQueryParams = {}) => {
    const qs = buildAlertsQuery(params);
    const url = qs ? `${FRAUD_ENDPOINTS.ALERTS_LIST}?${qs}` : FRAUD_ENDPOINTS.ALERTS_LIST;
    const { data } = await httpClient.get<ApiResponse<FraudAlertsResponse>>(url);
    return data;
};

export const getFraudAlertDetails = async (alertId: string) => {
    const { data } = await httpClient.get<ApiResponse<FraudAlertDetails>>(
        FRAUD_ENDPOINTS.ALERT_DETAILS(alertId)
    );
    return data;
};

export const updateAlertStatus = async (alertId: string, body: UpdateAlertStatusRequest) => {
    const { data } = await httpClient.put<ApiResponse<UpdateAlertStatusResponse>>(
        FRAUD_ENDPOINTS.ALERT_UPDATE_STATUS(alertId),
        body
    );
    return data;
};

// ============================================================================
// Fraud Stats API
// ============================================================================

export const getFraudStats = async () => {
    const { data } = await httpClient.get<ApiResponse<FraudStats>>(FRAUD_ENDPOINTS.STATS);
    return data;
};

// ============================================================================
// Investigation Cases API
// ============================================================================

export const getInvestigationCases = async (params: CasesQueryParams = {}) => {
    const qs = buildCasesQuery(params);
    const url = qs ? `${FRAUD_ENDPOINTS.CASES_LIST}?${qs}` : FRAUD_ENDPOINTS.CASES_LIST;
    const { data } = await httpClient.get<ApiResponse<CasesResponse>>(url);
    return data;
};

export const getCaseDetails = async (caseIdOrNumber: string) => {
    const { data } = await httpClient.get<ApiResponse<InvestigationCaseDetails>>(
        FRAUD_ENDPOINTS.CASE_DETAILS(caseIdOrNumber)
    );
    return data;
};

export const createCase = async (body: CreateCaseRequest) => {
    const { data } = await httpClient.post<ApiResponse<CreateCaseResponse>>(
        FRAUD_ENDPOINTS.CASE_CREATE,
        body
    );
    return data;
};

export const updateCase = async (caseId: string, body: UpdateCaseRequest) => {
    const { data } = await httpClient.put<ApiResponse<UpdateCaseResponse>>(
        FRAUD_ENDPOINTS.CASE_UPDATE(caseId),
        body
    );
    return data;
};

// ============================================================================
// User Risk & Verification API
// ============================================================================

export const getUserRiskScore = async (userId: string) => {
    const { data } = await httpClient.get<ApiResponse<UserRiskScore>>(
        FRAUD_ENDPOINTS.USER_RISK_SCORE(userId)
    );
    return data;
};

export const requestReverification = async (userId: string, body: RequestReverificationRequest) => {
    const { data } = await httpClient.post<ApiResponse<RequestReverificationResponse>>(
        FRAUD_ENDPOINTS.REQUEST_REVERIFICATION(userId),
        body
    );
    return data;
};

// ============================================================================
// Fraud Detection API
// ============================================================================

export const runSuspiciousPaymentsDetection = async () => {
    const { data } = await httpClient.post<ApiResponse<DetectionResponse>>(
        FRAUD_ENDPOINTS.DETECT_SUSPICIOUS_PAYMENTS
    );
    return data;
};

export const runHighCancellationDetection = async () => {
    const { data } = await httpClient.post<ApiResponse<DetectionResponse>>(
        FRAUD_ENDPOINTS.DETECT_HIGH_CANCELLATION
    );
    return data;
};

export const runFakeBookingsDetection = async () => {
    const { data } = await httpClient.post<ApiResponse<DetectionResponse>>(
        FRAUD_ENDPOINTS.DETECT_FAKE_BOOKINGS
    );
    return data;
};

export const runAllDetection = async () => {
    const { data } = await httpClient.post<ApiResponse<RunAllDetectionResponse>>(
        FRAUD_ENDPOINTS.DETECT_RUN_ALL
    );
    return data;
};
